import cx from 'classnames';
import { FormattedMessage } from 'react-intl';

import Divider from 'components/divider';
import { Link } from 'components/router';
import { Text } from 'components/typography';

import customContentMap from '../custom-content-map';
import TalkTypes from '../talk-types';
import type TalkEventBlurbProps from './TalkEventBlurb.props';

const TalkEventBlurb = ({
  id,
  featured,
  type,
  partnerName,
  videoContext,
  className
}: TalkEventBlurbProps): React.ReactNode => {
  const customContentDetails = customContentMap[id] ?? null;

  const featuredStatement = "TED's editors chose to feature it for you.";
  const curationStatement = 'Read our curation guidelines.';

  const eventTypeMap = {
    [TalkTypes.TedStageTalk]: {
      blurb: `This talk was presented at an official TED conference. `,
      link: {
        url: '/about/our-organization/how-ted-works/ted-s-curation-guidelines',
        text: curationStatement
      }
    },
    [TalkTypes.TedXTalk]: {
      blurb: `This talk was presented to a local audience ${
        videoContext !== '' ? `at ${videoContext}, an independent event` : ''
      }. ${featured ? featuredStatement : ''}`,
      link: {
        url: '/about/programs-initiatives/tedx-program',
        text: 'Read more about TEDx.'
      }
    },
    [TalkTypes.TedEdOriginal]: {
      blurb: `This video was produced by TED-Ed, TED’s youth and education initiative. ${
        featured ? featuredStatement : ''
      }`,
      link: {
        url: 'https://ed.ted.com/',
        text: 'Read more about TED-Ed.'
      }
    },
    [TalkTypes.BestOfWeb]: {
      blurb: `This video is a TED "Best of the Web" pick, featuring a remarkable idea available on the internet from an independent creator. This idea didn’t come from TED, but TED is thrilled to spread it. ${
        featured ? featuredStatement : ''
      }`
    },
    [TalkTypes.TedSalonTalk]: {
      blurb: `This talk was presented at a TED Salon event ${
        partnerName !== '' ? `given in partnership with ${partnerName}` : ''
      }. ${featured ? featuredStatement : ''}`,
      link: {
        url: '/attend/conferences/special-events/ted-salons',
        text: 'Read more about TED Salons.'
      }
    },
    [TalkTypes.CustomSponsoredContent]: {
      blurb:
        customContentDetails?.blurb ??
        `Commissioned by ${
          partnerName !== null ? partnerName : customContentDetails?.partnerName
        } and created in partnership with Ideas Studio, the branded content arm of TED Partnerships`
    }
  };

  if (!eventTypeMap[type.id]) {
    return null;
  }

  return (
    <div className={cx('mb-12', [className])}>
      <Divider className="mb-4 hidden lg:block" />
      <Text size="m" className="mr-1 inline text-gray-700">
        <FormattedMessage
          defaultMessage="{blurb}"
          values={{ blurb: eventTypeMap[type.id].blurb }}
        />
      </Text>
      {eventTypeMap[type.id].link && (
        <Link
          className="text-sm italic text-gray-700 underline"
          href={eventTypeMap[type.id].link.url}
        >
          <FormattedMessage
            defaultMessage="{text}"
            values={{
              text: eventTypeMap[type.id].link.text
            }}
          />
        </Link>
      )}
    </div>
  );
};

export default TalkEventBlurb;
